import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Col, Form, Row, Table } from 'react-bootstrap';
import SwalDialog from 'lib/utils/toast';

import {
  ATTRIBUTE_SCORE_GROUP_BY_SECTION,
  ATTRIBUTE_SCORE_USER_INPUT_COUNTS,
} from 'lib/networking/endpoints';
import {
  l0BeverageCategoryId,
  l0FoodCategoryId,
  l0OperationalsCategoryId,
} from 'lib/enums/TaxonomyLevel';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';

const INITIAL_USER_INPUTS = { food: 0, bev: 0, ops: 0 };

/**
 * Displayed instead of the subsection table when the selected category is a
 * section-level scoring section (Claims or Top-Off).
 *
 * Shows one row:
 * Total Attributes
 * | Food (System) | Food (User) | Bev (System) | Bev (User) | Ops (System) | Ops (User)
 *
 * "System" values are aggregated from the already-loaded sectionSubData prop.
 * "User" inputs are fetched from the attribute-score/group-by-section endpoint and
 * saved back via attribute-score/user-input-counts.
 */
function SectionLevelScoringView({ selectedCategory, sectionSubData }) {
  const [isEditing, setIsEditing] = useState(false);
  const [userInputs, setUserInputs] = useState(INITIAL_USER_INPUTS);

  // Aggregate system counts from all subsections for this section.
  const systemCounts = Object.values(sectionSubData || {}).reduce(
    (acc, sub) => ({
      total: acc.total + (sub.total_attributes || 0),
      food: acc.food + (sub.fd_total_attributes || 0),
      bev: acc.bev + (sub.bev_total_attributes || 0),
      ops: acc.ops + (sub.ops_total_attributes || 0),
    }),
    { total: 0, food: 0, bev: 0, ops: 0 },
  );

  const { data: { data: sectionScoreData = {} } = {} } = useGet(ATTRIBUTE_SCORE_GROUP_BY_SECTION);

  useEffect(() => {
    if (!selectedCategory || !sectionScoreData) return;
    const sectionEntry = sectionScoreData[selectedCategory.label];
    if (!sectionEntry) return;
    const l0Scores = sectionEntry.l0_scores || {};
    setUserInputs({
      food: l0Scores[l0FoodCategoryId.toString()]?.user_input_total_attributes_count ?? 0,
      bev: l0Scores[l0BeverageCategoryId.toString()]?.user_input_total_attributes_count ?? 0,
      ops: l0Scores[l0OperationalsCategoryId.toString()]?.user_input_total_attributes_count ?? 0,
    });
  }, [selectedCategory, sectionScoreData]);

  const { postData: saveUserInputCounts } = usePost(
    ATTRIBUTE_SCORE_USER_INPUT_COUNTS,
    () => {
      SwalDialog('success', 'User input counts updated.');
      setIsEditing(false);
    },
    error => SwalDialog('error', `An error occurred: ${error?.response?.data?.message}`, 'Error'),
  );

  function handleSave() {
    const sectionEntry = sectionScoreData[selectedCategory.label] || {};
    saveUserInputCounts({
      data: [
        {
          l0_category_id: l0FoodCategoryId,
          attribute_section_id: sectionEntry.section_id,
          user_input_total_attributes_count: Number(userInputs.food),
        },
        {
          l0_category_id: l0BeverageCategoryId,
          attribute_section_id: sectionEntry.section_id,
          user_input_total_attributes_count: Number(userInputs.bev),
        },
        {
          l0_category_id: l0OperationalsCategoryId,
          attribute_section_id: sectionEntry.section_id,
          user_input_total_attributes_count: Number(userInputs.ops),
        },
      ],
    });
  }

  return (
    <>
      <Row className="mt-2">
        <Col className="text-right">
          <Button
            variant={isEditing ? 'danger' : 'primary'}
            onClick={isEditing ? handleSave : () => setIsEditing(true)}
          >
            {isEditing ? 'Save User Inputs' : 'Edit User Inputs'}
          </Button>
        </Col>
      </Row>
      <Table bordered hover className="mt-3 text-center">
        <thead>
          <tr>
            <th>Total Attributes</th>
            <th>Food (System)</th>
            <th>Food (User)</th>
            <th>Beverage (System)</th>
            <th>Beverage (User)</th>
            <th>Operations (System)</th>
            <th>Operations (User)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="pt-3">{systemCounts.total}</td>
            <td className="pt-3">{systemCounts.food}</td>
            <td>
              <Form.Control
                className="text-center"
                type="number"
                min="0"
                value={userInputs.food}
                onChange={e => setUserInputs(prev => ({ ...prev, food: e.target.value }))}
                disabled={!isEditing}
              />
            </td>
            <td className="pt-3">{systemCounts.bev}</td>
            <td>
              <Form.Control
                className="text-center"
                type="number"
                min="0"
                value={userInputs.bev}
                onChange={e => setUserInputs(prev => ({ ...prev, bev: e.target.value }))}
                disabled={!isEditing}
              />
            </td>
            <td className="pt-3">{systemCounts.ops}</td>
            <td>
              <Form.Control
                className="text-center"
                type="number"
                min="0"
                value={userInputs.ops}
                onChange={e => setUserInputs(prev => ({ ...prev, ops: e.target.value }))}
                disabled={!isEditing}
              />
            </td>
          </tr>
        </tbody>
      </Table>
    </>
  );
}

SectionLevelScoringView.propTypes = {
  selectedCategory: PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
  }).isRequired,
  sectionSubData: PropTypes.object,
};

export default SectionLevelScoringView;
